import CartService from "../../../services/CartService";
import { Api } from "../../../services/Api";
import { CHECKOUT_URL, firstError, updateCart } from "./cart";
import { dismissKeyboard, jumpBy, scrollToElement } from "./scroll";

const GENERATED_EMAIL_DOMAIN = "orders.debrand.com";

// Breathing room (px) between the top of the viewport and a step's title when
// the checkout scrolls to it.
const STEP_SCROLL_OFFSET = 32;

// CSRF inputs are re-masked on every render, so ignore them when deciding
// whether a region actually changed.
const normalize = (html) =>
    html.replace(/(name="CRAFT_CSRF_TOKEN"[^>]*value=")[^"]*"/g, '$1"');

export default (initial) => {
    // Methods here are also called from nested components (SpcAddressForm,
    // SpcGifting), where `this.$root` is that component's element, so hold on
    // to the checkout root instead.
    let root;

    return {
        email: initial.email,
        emailSkipped: initial.email.includes(GENERATED_EMAIL_DOMAIN),
        emailError: "",
        skipEmailConfirmOpen: false,

        // Requests the customer is waiting on. While any are in flight, every
        // Continue button and option is disabled (`busy`), and the button that
        // started it (`busyStep`) shows a spinner.
        inFlight: 0,
        busyStep: null,

        // Read out by the page's status live region (index.twig).
        announcement: "",

        signInOpen: false,
        signInKnownAccount: false,
        // The email the customer chose to check out as a guest with, so
        // continuing again doesn't re-open sign-in (or re-send a code).
        guestEmail: null,
        placingOrder: false,
        cardTokenized: false,

        state: {},
        regions: {},
        pending: [],

        // The one expanded step (accordion), or null when all are collapsed.
        openStep: null,

        init() {
            root = this.$el;

            // Closing the sign-in prompt checkout opened (close button,
            // overlay, Escape) means "not now", the same as Continue as guest,
            // so continuing again doesn't reopen it or send another code.
            this.$watch("signInOpen", (open) => {
                if (!open && this.signInKnownAccount) {
                    this.guestEmail = this.email;
                }
            });

            // Runs before Alpine walks the children, so this is the server HTML.
            root.querySelectorAll("[data-spc-region]").forEach((el) => {
                this.regions[el.dataset.spcRegion] = normalize(el.outerHTML);
            });
            this.readState();
            this.openStep = this.firstIncompleteStep();

            // A region the customer has typed into is "dirty" until it is saved
            // or re-rendered, so a save elsewhere can't wipe their input. Radios
            // (shipping method, payment choice) save as soon as they change.
            root.addEventListener("input", (event) => {
                if (event.target.type === "radio") {
                    return;
                }
                const region = event.target.closest("[data-spc-region]");
                if (region) {
                    region.dataset.spcDirty = "1";
                }
            });

            // The RedFire card iframe has no accessible name (fix belongs in
            // reusser/craft-redfire-payments' paymentForm.twig).
            root.querySelectorAll(
                '[id$="redFirePaymentForm"] iframe:not([title])',
            ).forEach((frame) => {
                frame.title = "Card details";
            });

            // The RedFire tokenizer iframe posts this once the card is valid.
            window.addEventListener("message", (message) => {
                if (message.data === "tokenized") {
                    this.cardTokenized = true;
                }
            });

            // Payment forms hold one-time tokens; don't reuse a bfcache copy.
            window.addEventListener("pageshow", (event) => {
                if (event.persisted) {
                    window.location.reload();
                }
            });
        },

        get busy() {
            return this.inFlight > 0;
        },

        async whileBusy(action) {
            this.inFlight++;
            try {
                return await action();
            } finally {
                this.inFlight--;
            }
        },

        // Run a step's Continue action with a spinner on that step's button.
        async run(step, action) {
            if (this.busy) {
                return;
            }
            this.busyStep = step;
            try {
                await this.whileBusy(action);
            } finally {
                this.busyStep = null;
            }
        },

        // Clear first so the same message twice is still read out.
        announce(message) {
            this.announcement = "";
            setTimeout(() => {
                this.announcement = message;
            }, 100);
        },

        get paymentReady() {
            return !this.state.needsTokenizedCard || this.cardTokenized;
        },

        readState() {
            const el = root.querySelector("[data-spc-state]");
            this.state = el ? JSON.parse(el.textContent) : {};
        },

        // Steps come from the server (_sections/state.twig), in display order.
        // A step unlocks once every step before it is complete, because later
        // steps depend on earlier ones (no shipping rates without an address).
        steps() {
            return this.state.steps ?? [];
        },

        isComplete(key) {
            return (
                this.steps().find((step) => step.key === key)?.complete ?? false
            );
        },

        // A step with nothing to choose (one shipping method), filled in for
        // the customer and skipped over once complete.
        isAuto(key) {
            return this.steps().find((step) => step.key === key)?.auto ?? false;
        },

        isUnlocked(key) {
            const steps = this.steps();
            const index = steps.findIndex((step) => step.key === key);
            return (
                index !== -1 &&
                steps.slice(0, index).every((step) => step.complete)
            );
        },

        isOpen(key) {
            return this.openStep === key && this.isUnlocked(key);
        },

        toggle(key) {
            if (!this.isUnlocked(key)) {
                return;
            }
            if (this.openStep !== key) {
                this.openStep = key;
                return;
            }
            // Collapsing a step leaves the first unfinished one open (Payment,
            // once everything else is done) rather than nothing at all.
            const next = this.firstIncompleteStep();
            this.openStep = next !== key ? next : null;
        },

        firstIncompleteStep() {
            return this.steps().find((step) => !step.complete)?.key ?? null;
        },

        // The step after `key`, passing over completed auto steps.
        nextStep(key) {
            const steps = this.steps();
            let index = steps.findIndex((step) => step.key === key) + 1;
            while (steps[index]?.auto && steps[index].complete) {
                index++;
            }
            return steps[index];
        },

        continueLabel(key) {
            return this.nextStep(key)?.continueLabel ?? "";
        },

        // Collapse a finished step and open the next one. If that one is still
        // locked (the save didn't complete this step), stay on the first
        // incomplete step instead.
        async advance(key) {
            const next = this.nextStep(key);
            await this.show(
                next && this.isUnlocked(next.key)
                    ? next.key
                    : this.firstIncompleteStep(),
            );
        },

        // Open a step, glide it to just below the top of the viewport and put
        // focus on it.
        async show(step) {
            if (!step || !this.isUnlocked(step)) {
                return;
            }
            const toggle = document.getElementById(`spc-${step}-toggle`);

            // On phones, let the keyboard finish closing first; the viewport
            // resizing mid-scroll is what made it lurch.
            await dismissKeyboard();
            const before = toggle?.getBoundingClientRect().top;

            this.openStep = step;
            await this.$nextTick();

            if (!toggle) {
                return;
            }
            // Collapsing the finished step shrinks the page above the next one,
            // so first put that step back where it was on screen, then glide it
            // to just below the top of the viewport.
            jumpBy(toggle.getBoundingClientRect().top - before);
            scrollToElement(toggle, STEP_SCROLL_OFFSET);

            // The button that was pressed is in the step that just collapsed;
            // put keyboard and screen reader focus on the step that opened.
            toggle.focus({ preventScroll: true });
        },

        // Remember in-flight saves so placing an order can wait for them.
        track(promise) {
            this.pending.push(promise);
            promise.finally(() => {
                this.pending = this.pending.filter((p) => p !== promise);
            });
            return promise;
        },

        async flush() {
            await Promise.allSettled(this.pending);
        },

        async save(data, region = null) {
            // Choosing a shipping/payment option by keyboard saves straight
            // away; the radio is disabled during the save and its region is
            // re-rendered, so put focus back on the group's checked radio.
            const active = document.activeElement;
            const focusedGroup =
                active?.matches?.('input[type="radio"]') && active.name;

            this.inFlight++;
            try {
                await this.track(updateCart(data));
                await this.refresh(region);
            } finally {
                this.inFlight--;
                if (focusedGroup) {
                    await this.$nextTick();
                    root.querySelector(
                        `input[type="radio"][name="${CSS.escape(focusedGroup)}"]:checked`,
                    )?.focus({ preventScroll: true });
                }
            }
        },

        async continueContact() {
            if (this.emailSkipped) {
                this.advance("contact");
                return;
            }

            const input = root.querySelector("#spc-email");
            if (!this.email || (input && !input.checkValidity())) {
                input?.reportValidity();
                return;
            }

            await this.run("contact", async () => {
                try {
                    await this.save({ email: this.email }, "contact");
                    this.emailError = "";
                } catch (e) {
                    this.emailError = firstError(e, "email");
                    return;
                }

                if (await this.emailHasAccount()) {
                    this.openSignIn(true);
                    return;
                }
                this.advance("contact");
            });
        },

        // Only offer sign-in to customers who have an account; anyone else
        // carries straight on as a guest. A failed lookup never blocks checkout.
        async emailHasAccount() {
            if (!initial.offerSignIn || this.email === this.guestEmail) {
                return false;
            }

            try {
                const res = await Api.post(
                    "/actions/debrand/login/check-email",
                    {
                        email: this.email,
                    },
                );
                return Boolean(res.data.hasAccount);
            } catch (e) {
                console.error(e);
                return false;
            }
        },

        // "Sign in" in the Contact header (open or collapsed), or an email
        // that has an account. `knownAccount`: send the code straight away.
        openSignIn(knownAccount = false) {
            this.signInKnownAccount = knownAccount;
            this.signInOpen = true;
            window.dispatchEvent(
                new CustomEvent("start-login", {
                    detail: {
                        target: "checkout",
                        email: this.email,
                        send: knownAccount,
                    },
                }),
            );
        },

        continueAsGuest() {
            this.signInOpen = false;
            this.guestEmail = this.email;
            if (this.isComplete("contact")) {
                this.advance("contact");
            }
        },

        // Same warning as the legacy step's confirm(), as a site modal
        // (_sections/_skip-email-modal.twig).
        skipEmail() {
            this.skipEmailConfirmOpen = true;
        },

        async confirmSkipEmail() {
            this.skipEmailConfirmOpen = false;

            await this.run("contact", async () => {
                try {
                    const res = await Api.post(
                        "/actions/debrand/cart/skip-email",
                    );
                    if (!res.data.success) {
                        throw new Error(res.data.message);
                    }
                    this.email = res.data.email;
                    this.emailSkipped = true;
                    this.emailError = "";
                    await this.refresh("contact");
                    this.advance("contact");
                } catch (e) {
                    console.error(e);
                    this.emailError =
                        "There was an error while trying to skip the email. Please reload the page and try again.";
                }
            });
        },

        undoSkipEmail() {
            this.email = "";
            this.emailSkipped = false;
        },

        // CodeLogin (inline) signed the customer in. The session and CSRF token
        // changed, and the cart now belongs to the user, so re-render in place.
        async onSignedIn() {
            this.signInOpen = false;

            await this.whileBusy(async () => {
                const res = await Api.get("/actions/users/session-info");
                document
                    .querySelectorAll('input[name="CRAFT_CSRF_TOKEN"]')
                    .forEach((input) => {
                        input.value = res.data.csrfTokenValue;
                    });

                // Signing in changes every section (saved addresses, account),
                // so re-render all of them, typed-in or not.
                await this.refresh("*");
            });

            if (this.state.email) {
                this.email = this.state.email;
                this.emailSkipped = false;
            }
            this.advance("contact");
        },

        async saveShippingMethod(handle) {
            try {
                await this.save({ shippingMethodHandle: handle }, "shipping");
                this.announce(
                    `Shipping method updated. Order total: ${this.state.totalFormatted}`,
                );
            } catch (e) {
                alert(firstError(e, "shippingMethodHandle"));
                await this.refresh();
            }
        },

        async saveGateway(gatewayId) {
            try {
                this.cardTokenized = false;
                await this.save(
                    { gatewayId, paymentSourceId: null },
                    "payment-options",
                );
            } catch (e) {
                alert(firstError(e, "gatewayId"));
                await this.refresh();
            }
        },

        async savePaymentSource(paymentSourceId) {
            try {
                await this.save({ paymentSourceId }, "payment-options");
            } catch (e) {
                alert(firstError(e, "paymentSourceId"));
                await this.refresh();
            }
        },

        // Re-render the page server-side and swap in only the regions that changed,
        // so section state, totals and guards stay in step with the cart without a
        // full page reload. `savedRegion` is the region that was just saved ("*"
        // for all); other regions with unsaved input are left alone until saved.
        async refresh(savedRegion = null) {
            const res = await fetch(`${CHECKOUT_URL}?spcRefresh=1`, {
                credentials: "same-origin",
                headers: { Accept: "text/html" },
            });

            // An emptied or completed cart redirects away from checkout.
            if (res.redirected && new URL(res.url).pathname !== CHECKOUT_URL) {
                window.location.href = res.url;
                return;
            }

            const doc = new DOMParser().parseFromString(
                await res.text(),
                "text/html",
            );

            doc.querySelectorAll("[data-spc-region]").forEach((fresh) => {
                const name = fresh.dataset.spcRegion;
                const html = normalize(fresh.outerHTML);
                const current = root.querySelector(
                    `[data-spc-region="${name}"]`,
                );

                if (!current || this.regions[name] === html) {
                    return;
                }
                if (
                    current.dataset.spcDirty &&
                    savedRegion !== "*" &&
                    name !== savedRegion
                ) {
                    return;
                }

                this.regions[name] = html;
                current.replaceWith(document.importNode(fresh, true));
            });

            this.readState();

            // A change upstream (e.g. a P.O. Box address) can re-lock the open step.
            if (this.openStep && !this.isUnlocked(this.openStep)) {
                this.openStep = this.firstIncompleteStep();
            }

            // An auto step that just saved (the only shipping method) moves on.
            if (
                this.openStep &&
                this.isAuto(this.openStep) &&
                this.isComplete(this.openStep)
            ) {
                this.advance(this.openStep);
            }

            // Update the order summary. Dispatch from the root: the caller's
            // element may be in a region that was just swapped out, and a
            // detached element's event never reaches the summary's listener.
            await CartService.getCart({
                $dispatch: (name, detail) =>
                    root.dispatchEvent(
                        new CustomEvent(name, { detail, bubbles: true }),
                    ),
            });
        },

        async placeOrder(event) {
            if (this.placingOrder || this.busy || !this.paymentReady) {
                return;
            }

            this.placingOrder = true;
            await this.flush();
            event.target.submit();
        },
    };
};
